import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type { AxiosError } from 'axios'

interface BoQResponse {
  projectId: number
  categories: any[]
  totalCategories: number
  totalItems: number
  totalValue: number
}

interface BoQItem {
  id: number
  categoryId: number
  itemNumber?: string
  itemName: string
  specification?: Record<string, any>
  unit: string
  quantity: number
  unitPrice?: number
  totalPrice?: number
  bobot?: number
  notes?: string
  sortOrder?: number
}

interface BoQCategory {
  id: number
  projectId: number
  code: string
  name: string
  totalBobot?: number
  items: BoQItem[]
  totalValue?: number
  totalQty?: number
  itemCount?: number
  collapsed?: boolean
}

// ============================================
// Query Hooks (Fetching data)
// ============================================

/**
 * Hook to fetch all BoQ data for a project
 */
export function useBoQ(projectId: number) {
  return useQuery<BoQResponse, AxiosError>({
    queryKey: ['boq', projectId],
    queryFn: async () => {
      const response = await apiClient.getBoQByProject(projectId)
      return response.data
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes (formerly cacheTime)
  })
}

/**
 * Hook to fetch single BoQ item
 */
export function useBoQItem(itemId: number) {
  return useQuery<BoQItem, AxiosError>({
    queryKey: ['boq-item', itemId],
    queryFn: async () => {
      const response = await apiClient.getBoQItem(itemId)
      return response.data
    },
    enabled: !!itemId
  })
}

// ============================================
// Mutation Hooks (Modifying data)
// ============================================

/**
 * Hook to update single BoQ item
 */
export function useUpdateBoQItem(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<
    BoQItem,
    AxiosError,
    { itemId: number; data: Partial<BoQItem> }
  >({
    mutationFn: async ({ itemId, data }) => {
      const response = await apiClient.updateBoQItem(itemId, data)
      return response.data
    },
    onSuccess: (updatedItem, { itemId }) => {
      // Update cache
      queryClient.setQueryData(['boq-item', itemId], updatedItem)

      // Invalidate BoQ list to refetch
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId],
        exact: false
      })
    },
    onError: (error) => {
      console.error('Failed to update BoQ item:', error.response?.data)
    }
  })
}

/**
 * Hook to create new BoQ item
 */
export function useCreateBoQItem(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<BoQItem, AxiosError, Partial<BoQItem>>({
    mutationFn: async (itemData) => {
      const response = await apiClient.createBoQItem(itemData)
      return response.data
    },
    onSuccess: () => {
      // Invalidate BoQ list to refetch
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to create BoQ item:', error.response?.data)
    }
  })
}

/**
 * Hook to delete BoQ item
 */
export function useDeleteBoQItem(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean }, AxiosError, number>({
    mutationFn: async (itemId) => {
      const response = await apiClient.deleteBoQItem(itemId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to delete BoQ item:', error.response?.data)
    }
  })
}

/**
 * Hook for batch update (efficient for multiple changes)
 */
export function useBatchUpdateBoQItems(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<
    any,
    AxiosError,
    Array<{ id: number; data: Partial<BoQItem> }>
  >({
    mutationFn: async (updates) => {
      const response = await apiClient.batchUpdateBoQItems(updates)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to batch update BoQ items:', error.response?.data)
    }
  })
}

/**
 * Hook to create BoQ category
 */
export function useCreateBoQCategory(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<BoQCategory, AxiosError, Partial<BoQCategory>>({
    mutationFn: async (categoryData) => {
      const response = await apiClient.createBoQCategory({
        ...categoryData,
        projectId
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to create BoQ category:', error.response?.data)
    }
  })
}

/**
 * Hook to delete BoQ category
 */
export function useDeleteBoQCategory(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean }, AxiosError, number>({
    mutationFn: async (categoryId) => {
      const response = await apiClient.deleteBoQCategory(categoryId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to delete BoQ category:', error.response?.data)
    }
  })
}

/**
 * Hook to reset BoQ data (delete all categories and items for a project)
 */
export function useResetBoQ(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean }, AxiosError, void>({
    mutationFn: async () => {
      // Get all categories for this project
      const categoriesResponse = await apiClient.getBoQByProject(projectId)
      const categories = categoriesResponse.data.categories

      // Delete all categories (this will cascade to items)
      for (const category of categories) {
        await apiClient.deleteBoQCategory(category.id)
      }

      return { success: true }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to reset BoQ data:', error.response?.data)
    }
  })
}

/**
 * Hook to import BoQ data from CSV
 */
export function useImportBoQ(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<any, AxiosError, File>({
    mutationFn: async (file) => {
      const response = await apiClient.importBoQData(projectId, file)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['boq', projectId]
      })
      alert(`Import successful! ${data.importedCategories} categories and ${data.importedItems} items imported, ${data.updatedItems} items updated.`)
    },
    onError: (error) => {
      console.error('Failed to import BoQ data:', error.response?.data)
      const responseData = error.response?.data as { error?: string } | undefined
      const errorMessage = responseData?.error || error.message || 'Unknown error'
      alert(`Failed to import BoQ data: ${errorMessage}`)
    }
  })
}

